import {
    booleanAttribute,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import { combineLatest, Observable, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { v4 } from "uuid";

import exportedSass from "@hermes/aphrodite/design-system-tools";
import { Product } from "@hermes/api-model-product";
import { LoadGeoLibrariesService } from "@hermes/map-utils";
import {
    FeatureFlagService,
    PRODUCT_LOCATOR_ACTIVATION,
} from "@hermes/states/flipper";
import { ProductLocatorFacade } from "@hermes/states/product-locator";
import { ShopLocatorFacade } from "@hermes/states/shop-locator";
import { TrayFacade } from "@hermes/states/tray";

/**
 * Component to manage call to action for find-in-store button
 */
@Component({
    selector: "h-call-to-action-find-in-store",
    templateUrl: "./call-to-action-find-in-store.component.html",
    styleUrls: ["./call-to-action-find-in-store.component.scss"],
})
export class CallToActionFindInStoreComponent
    implements OnInit, OnDestroy, OnChanges
{
    // Inputs
    @ViewChild("angularLocateInStoreButtonInStock", {
        read: ElementRef,
    })
    public angularLocateInStoreButtonInStock: ElementRef<HTMLElement>;

    @Input()
    public product: Product;

    /**
     * Value of the SKU(s) to be used in the Product Locator
     */
    @Input()
    public skus: string[];

    /** apply transparent background if true*/
    @Input()
    public transparentBackground: boolean = false;

    /**
     * When disabled, button is still clickable but will emit `pressed` event without opening product locator
     */
    @Input({ transform: booleanAttribute })
    public shouldPreventProductLocatorLaunch = false;

    @Output()
    public readonly pressed = new EventEmitter<void>();

    public productLocatorIsActive$: Observable<boolean>;

    private subscription: Subscription = new Subscription();

    constructor(
        public productLocatorFacade: ProductLocatorFacade,
        public loadGeoLibrariesService: LoadGeoLibrariesService,
        private featureFlagService: FeatureFlagService,
        private trayFacade: TrayFacade,
        private shopLocatorFacade: ShopLocatorFacade,
    ) {}

    public ngOnChanges(changes: SimpleChanges): void {
        // If there are a new product or a new variation of the product, we have to reset the Product Locator ShopsStock & errors
        if (changes?.product?.currentValue) {
            this.productLocatorFacade.resetProductLocator();
        }
    }

    public ngOnInit(): void {
        this.productLocatorIsActive$ =
            this.featureFlagService.getFeatureFlagValue(
                PRODUCT_LOCATOR_ACTIVATION,
            );
        this.shopLocatorFacade.resetShopLocator();

        this.subscription.add(
            combineLatest([
                this.productLocatorFacade.readyToOpen$,
                this.loadGeoLibrariesService.isLoaded$,
            ])
                .pipe(
                    filter(
                        ([readyToOpen, isLoaded]) => readyToOpen && isLoaded,
                    ),
                )
                .subscribe(() => {
                    this.trayFacade.openTray({
                        hasOverlay: true,
                        noPaddingBottom: true,
                        noPaddingHorizontal: true,
                        hasCategoryInstitutional: false,
                        isOpen: true,
                        position: "right",
                        titleKey: "hermes.commerce.product_locator.title",
                        uuid: v4(),
                        name: "product-locator-tray",
                        metadata: { product: this.product },
                        backgroundColor:
                            exportedSass.secondaryColors["color-secondary"],
                        closeCallback: () => {
                            this.shopLocatorFacade.resetShopLocator();
                            this.productLocatorFacade.resetProductLocator();
                            this.angularLocateInStoreButtonInStock.nativeElement.focus();
                        },
                    });
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public launchProductLocator(): void {
        if (!this.shouldPreventProductLocatorLaunch) {
            this.productLocatorFacade.launchProductLocator(this.skus);
        }
        this.pressed.emit();
    }
}
